import React, { useState } from 'react';
import { FileText, ChevronRight, Loader, X } from 'lucide-react';

/**
 * GoogleFormsPickerModal
 * Shown when the backend cannot auto-detect the form from the respondent URL.
 * User selects the correct form; parent calls onSelect(formId).
 */
export default function GoogleFormsPickerModal({ forms, loading, onSelect, onClose }) {
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    await onSelect(selected);
    setConfirming(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="forms-picker-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-900">Select Your Google Form</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              We couldn't auto-detect the form. Select the one participants are filling out.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg" data-testid="forms-picker-close">
            <X size={16} />
          </button>
        </div>

        {/* Form list */}
        <div className="overflow-y-auto flex-1 px-3 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
              <Loader size={16} className="animate-spin" />
              <span className="text-xs">Loading forms…</span>
            </div>
          ) : forms.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-8">
              No Google Forms found in your account.
            </p>
          ) : (
            <ul className="space-y-1">
              {forms.map((form) => {
                const isSelected = selected === form.id;
                return (
                  <li key={form.id}>
                    <button
                      onClick={() => setSelected(form.id)}
                      data-testid={`forms-picker-item-${form.id}`}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                        isSelected
                          ? 'bg-green-50 border border-green-300 text-green-900'
                          : 'hover:bg-gray-50 border border-transparent text-gray-800'
                      }`}
                    >
                      <FileText
                        size={15}
                        className={isSelected ? 'text-green-600 flex-shrink-0' : 'text-gray-400 flex-shrink-0'}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{form.name}</p>
                        {form.modified && (
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            Last modified: {formatDate(form.modified)}
                          </p>
                        )}
                      </div>
                      {isSelected && <ChevronRight size={14} className="text-green-600 flex-shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selected || confirming}
            data-testid="forms-picker-confirm"
            className="flex items-center gap-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors"
          >
            {confirming ? (
              <>
                <Loader size={12} className="animate-spin" />
                Loading form…
              </>
            ) : (
              'Use this form'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
